import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ArrowLeft, DollarSign, Clock, Package, Cog, CheckCircle, XCircle, Camera } from 'lucide-react';

function ConfBadge({ level }) {
  const cls = level === 'high' ? 'bg-emerald-50 text-emerald-600' : level === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{level}</span>;
}

export default function MachineDetailPage() {
  const { machineId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/analytics/machine-detail/${machineId}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [machineId]);

  if (loading) return <div className="animate-pulse h-96 bg-slate-100 rounded-lg" />;
  if (!data) return <div className="text-center py-16 text-slate-400">Machine not found</div>;

  const { machine, kpis, spend_trend, product_ranking, changeout_history } = data;

  const kpiCards = [
    { label: 'Total Spend', value: `₹${kpis.total_spend.toLocaleString()}`, icon: DollarSign, color: 'text-orange-600 bg-orange-50' },
    { label: 'Avg Cost/Hr', value: `₹${kpis.avg_cost_per_hour.toFixed(2)}`, icon: Clock, color: 'text-blue-600 bg-blue-50' },
    { label: 'Total Hours', value: `${kpis.total_hours.toLocaleString()}h`, icon: Clock, color: 'text-violet-600 bg-violet-50' },
    { label: 'Products Used', value: kpis.product_count, icon: Package, color: 'text-amber-600 bg-amber-50' },
    { label: 'Change-outs', value: kpis.change_outs, icon: Cog, color: 'text-slate-600 bg-slate-100' },
  ];

  return (
    <div data-testid="machine-detail-page">
      <div className="mb-6">
        <Link to={machine.site_id ? `/analytics/sites/${machine.site_id}` : '/analytics'} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 mb-3" data-testid="back-to-site"><ArrowLeft size={14} />Back to {machine.site_name || 'Analytics'}</Link>
        <h1 className="page-title" data-testid="machine-detail-title">{machine.brand} {machine.model}</h1>
        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{machine.serial_number}</span>
          {machine.machine_class && <span className="text-xs text-slate-500">{machine.machine_class}</span>}
          {machine.bucket_capacity && <span className="text-xs text-slate-400">Bucket: {machine.bucket_capacity}</span>}
          <span className="text-xs text-slate-400">Site: {machine.site_name}</span>
          <span className="text-xs text-slate-400">{machine.company_name}</span>
          <Link to={`/analytics/machines/${machineId}/history`} className="text-xs text-blue-600 hover:underline font-medium" data-testid="view-cycle-history">View Cycle History</Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="machine-kpi-cards">
        {kpiCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="stat-card">
            <div className="flex items-center gap-2 mb-1"><Icon size={14} className={color.split(' ')[0]} /><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p></div>
            <p className="text-xl font-bold text-slate-900 font-heading">{value}</p>
          </Card>
        ))}
      </div>

      {/* Spend Trend + Product Ranking */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="stat-card" data-testid="machine-spend-trend">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Monthly Spend</h3>
          {spend_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={spend_trend}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={v => { const [y, m] = (v || '').split('-'); const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${names[parseInt(m,10)-1] || m} '${(y||'').slice(-2)}`; }} /><YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => `₹${Number(v).toLocaleString()}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="spend" fill="#0F172A" radius={[4,4,0,0]} /></BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-12">No spend data</p>}
        </Card>

        <Card className="stat-card" data-testid="machine-product-ranking">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Product Performance on This Machine</h3>
          {product_ranking.length > 0 ? (
            <div className="space-y-3">
              {product_ranking.map((p, i) => (
                <div key={p.product_id} className={`rounded-lg p-3 border ${i === 0 ? 'bg-emerald-50/50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-400">#{i + 1}</span>
                      <span className="text-sm font-medium text-slate-900">{p.brand}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${p.product_type === 'forging' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{p.product_type}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900">₹{p.cost_per_hour.toFixed(2)}<span className="text-xs font-normal text-slate-400">/hr</span></span>
                  </div>
                  <div className="flex gap-3 text-xs text-slate-500">
                    <span>Price: ₹{p.unit_price}</span>
                    <span>Life: {p.avg_life_hours.toFixed(0)}h</span>
                    <span>Spend: ₹{p.spend.toLocaleString()}</span>
                    <span>Trials: {p.count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-8">No product data</p>}
        </Card>
      </div>

      {/* Change-out History */}
      <Card className="stat-card" data-testid="machine-changeout-history">
        <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Change-out History</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Event</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Qty</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Strata</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reason</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Proof</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {changeout_history.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">No change-out records</TableCell></TableRow> :
              changeout_history.map((h, i) => (
                <TableRow key={i} className="hover:bg-slate-50/50">
                  <TableCell className="text-sm text-slate-900">{h.date}</TableCell>
                  <TableCell><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${h.event_type === 'installation' ? 'bg-emerald-50 text-emerald-600' : h.event_type === 'removal' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>{h.event_type}</span></TableCell>
                  <TableCell className="text-sm font-medium text-slate-900">{h.product}</TableCell>
                  <TableCell><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${h.product_type === 'forging' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{h.product_type}</span></TableCell>
                  <TableCell className="text-sm text-slate-600">{h.quantity || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{h.hours > 0 ? `${h.hours}h` : '-'}</TableCell>
                  <TableCell className="text-sm text-slate-500">{h.strata || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-500">{h.removal_reason || '-'}</TableCell>
                  <TableCell>{h.has_proof ? <Camera size={14} className="text-emerald-500" /> : <span className="text-xs text-slate-300">-</span>}</TableCell>
                  <TableCell><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${h.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : h.status === 'submitted' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>{h.status}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
